import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { navigationPath } from '../../App';
import { createUploadHandler } from './utils/file-upload';
import UrlField from './components/url-field';
import FloatingSaveButton from './components/floating-save-button';
import BannerUploadField from './components/banner-upload-field';
import { useHoverPreview } from './components/hover-preview';

interface Data7Hari {
  deposit: number;
  login: number;
  withdraw: number;
  date: string;
  register: number;
}

interface PgaDropdownProps {
  value: string;
  onChange: (value: string) => Promise<void>;
  isLoading: boolean;
}

const PgaDropdown = ({ value, onChange, isLoading }: PgaDropdownProps) => {
  const pgaOptions = [
    { value: 'IDN', label: 'IDN' },
    { value: 'PRESTIGE', label: 'PRESTIGE' },
    { value: 'MINERA', label: 'MINERA' },
  ];

  return (
    <div className='flex flex-col space-y-1'>
      <label className='text-sm font-medium text-blue-400 mb-2'>PGA Provider</label>
      <div className='relative'>
        <select
          value={value}
          onChange={e => onChange(e.target.value)}
          disabled={isLoading}
          className='w-full h-[45px] bg-[#212a40] text-white border border-gray-600 rounded-md px-4 focus:outline-none focus:border-blue-500 disabled:opacity-60 disabled:cursor-not-allowed'
        >
          {pgaOptions.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {isLoading && (
          <div className='absolute right-3 top-1/2 transform -translate-y-1/2'>
            <div className='animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-blue-500'></div>
          </div>
        )}
      </div>
    </div>
  );
};

const Home = () => {
  const navigate = useNavigate();
  const { handleMouseMove, handleMouseLeave, HoverPreviewComponent } = useHoverPreview();

  const [id, setId] = useState('');
  const [banner1, setBanner1] = useState('');
  const [banner2, setBanner2] = useState('');
  const [banner3, setBanner3] = useState('');
  const [linkRTP, setLinkRTP] = useState('');
  const [bannerLiveGames, setBannerLiveGames] = useState('');
  const [livedrawSingapore, setLivedrawSingapore] = useState('');
  const [livedrawCambodia, setLivedrawCambodia] = useState('');
  const [livedrawChina, setLivedrawChina] = useState('');
  const [livedrawSydney, setLivedrawSydney] = useState('');
  const [livedrawHongkong, setLivedrawHongkong] = useState('');
  const [livedrawMacau, setLivedrawMacau] = useState('');
  const [livedrawTaiwan, setLivedrawTaiwan] = useState('');
  const [livedrawSingaporeToto, setLivedrawSingaporeToto] = useState('');
  const [livechat, setLivechat] = useState('');
  const [pga, setPga] = useState('IDN');
  const [maintenance, setMaintenance] = useState(false);
  const [dataPengeluaran, setDataPengeluaran] = useState('');
  const [data7hari, setData7hari] = useState<Data7Hari[]>([]);
  const [uploading, setUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isPgaLoading, setIsPgaLoading] = useState(false);

  const uploadHandler = createUploadHandler(setUploading, {
    navigate,
    navigateTo: navigationPath.dashboard,
    successMessage: 'Berhasil Upload',
    errorMessage: 'Terjadi kesalahan ketika upload.',
  });

  const handleBannerUpload = (file: File, endpoint: string) => {
    return uploadHandler(file, { endpoint });
  };

  const handleFileSelect1 = (file: File) => {
    return handleBannerUpload(file, '/api/cms/upload-1');
  };

  const handleFileSelect2 = (file: File) => {
    return handleBannerUpload(file, '/api/cms/upload-2');
  };

  const handleFileSelect3 = (file: File) => {
    return handleBannerUpload(file, '/api/cms/upload-3');
  };

  const handleFileSelect4 = (file: File) => {
    return handleBannerUpload(file, '/api/cms/upload-games');
  };

  useEffect(() => {
    fetch(`/api/cms/get-data-home`)
      .then(response => response.json())
      .then(res => {
        setId(res.data.id);
        setBanner1(res.data.banner1);
        setBanner2(res.data.banner2);
        setBanner3(res.data.banner3);
        setLinkRTP(res.data.linkRTP);
        setBannerLiveGames(res.data.bannerLiveGames);
        setLivedrawSingapore(res.data.livedrawSingapore);
        setLivedrawCambodia(res.data.livedrawCambodia);
        setLivedrawChina(res.data.livedrawChina);
        setLivedrawHongkong(res.data.livedrawHongkong);
        setLivedrawMacau(res.data.livedrawMacau);
        setLivedrawSydney(res.data.livedrawSydney);
        setLivedrawTaiwan(res.data.livedrawTaiwan);
        setLivedrawSingaporeToto(res.data.livedrawSingaporeToto);
        setLivechat(res.data.livechat);
        setMaintenance(res.data.maintenance);
        setDataPengeluaran(res.data.dataPengeluaran);
        setPga(res.data.pga);
      });
  }, [uploading]);

  useEffect(() => {
    fetch(`/api/cms/get-all-data-user-7day`)
      .then(response => response.json())
      .then(res => {
        setData7hari(res.data7hari);
      });
  }, []);

  const save = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`/api/cms/update-home`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          maintenance,
          banner1,
          banner2,
          banner3,
          bannerLiveGames,
          livedrawSingapore,
          livedrawCambodia,
          livedrawChina,
          livedrawHongkong,
          livedrawMacau,
          livedrawSydney,
          livedrawTaiwan,
          livedrawSingaporeToto,
          livechat,
          dataPengeluaran,
          linkRTP,
          id,
        }),
      });
      const res = await response.json();
      if (!response.ok) throw new Error(res.errors[0].message || res.message);
      toast.success('Berhasil mengupdate data HOME');
      setIsLoading(false);
      navigate(navigationPath.dashboard);
    } catch (error: any) {
      toast.error(error.message || `Terjadi kesalahan ketika menyimpan data home`);
      setIsLoading(false);
    }
  };

  const formattedChartData = data7hari.map(item => ({
    name: new Date(new Date(item.date).getTime() - 7 * 60 * 60 * 1000).toLocaleDateString('id-ID', { weekday: 'short' }),
    deposit: item.deposit,
    withdraw: item.withdraw,
    login: item.login,
    register: item.register,
  }));

  const handlePgaChange = async (newPgaValue: string) => {
    setPga(newPgaValue);
    setIsPgaLoading(true);

    try {
      const response = await fetch(`/api/cms/toggle-pga`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          pga: newPgaValue,
          id,
        }),
      });

      const res = await response.json();
      if (!response.ok) throw new Error(res.errors?.[0]?.message || res.message);
      toast.success('Berhasil mengubah PGA provider');
    } catch (error: any) {
      toast.error(error.message || 'Terjadi kesalahan ketika mengubah PGA provider');
      setPga(pga); // Reset to previous value if error occurs
    } finally {
      setIsPgaLoading(false);
    }
  };

  return (
    <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
      {/* Banner Management */}
      <div className='bg-[#1c243b] rounded-lg shadow-lg p-6'>
        <h2 className='text-xl font-semibold mb-4 text-white'>Banner Management</h2>
        <div className='space-y-3'>
          <BannerUploadField
            label='Banner 1'
            value={banner1}
            onChange={setBanner1}
            onUpload={handleFileSelect1}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
          />
          <BannerUploadField
            label='Banner 2'
            value={banner2}
            onChange={setBanner2}
            onUpload={handleFileSelect2}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
          />
          <BannerUploadField
            label='Banner 3'
            value={banner3}
            onChange={setBanner3}
            onUpload={handleFileSelect3}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
          />
          <BannerUploadField
            label='Banner Live Games'
            value={bannerLiveGames}
            onChange={setBannerLiveGames}
            onUpload={handleFileSelect4}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
          />
        </div>
      </div>

      {/* URL Configuration */}
      <div className='bg-[#1c243b] rounded-lg shadow-lg p-6 flex flex-col min-h-[500px]'>
        <h2 className='text-xl font-semibold mb-4 text-white'>URL Configuration</h2>

        <div className='space-y-3 flex-grow'>
          <UrlField label='URL Live Chat' value={livechat} onChange={setLivechat} />
          <UrlField label='URL Data Pengeluaran' value={dataPengeluaran} onChange={setDataPengeluaran} />
          <UrlField label='URL RTP' value={linkRTP} onChange={setLinkRTP} />

          {/* PGA Dropdown */}
          <PgaDropdown value={pga} onChange={handlePgaChange} isLoading={isPgaLoading} />
        </div>

        <div className='mt-auto'>
          <div className='flex items-center justify-between py-4 px-4 bg-[#212a40] rounded-md'>
            <span className='text-white font-medium'>Maintenance Mode</span>
            <label className='relative inline-flex items-center cursor-pointer'>
              <input
                type='checkbox'
                checked={maintenance}
                onChange={() => setMaintenance(!maintenance)}
                className='sr-only peer'
              />
              <div className="w-11 h-6 bg-gray-700 rounded-full peer peer-focus:ring-4 peer-focus:ring-blue-800 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all border-gray-600 peer-checked:bg-blue-600"></div>
            </label>
          </div>
        </div>
      </div>

      {/* LiveDraw URLs */}
      <div className='bg-[#1c243b] rounded-lg shadow-lg p-6'>
        <h2 className='text-xl font-semibold mb-4 text-white'>LiveDraw URLs</h2>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
          <UrlField label='URL Live Draw Sydney' value={livedrawSydney} onChange={setLivedrawSydney} />
          <UrlField label='URL Live Draw Singapore' value={livedrawSingapore} onChange={setLivedrawSingapore} />
          <UrlField label='URL Live Draw Hongkong' value={livedrawHongkong} onChange={setLivedrawHongkong} />
          <UrlField label='URL Live Draw Cambodia' value={livedrawCambodia} onChange={setLivedrawCambodia} />
          <UrlField label='URL Live Draw China' value={livedrawChina} onChange={setLivedrawChina} />
          <UrlField label='URL Live Draw Taiwan' value={livedrawTaiwan} onChange={setLivedrawTaiwan} />
          <UrlField label='URL Live Draw Macau' value={livedrawMacau} onChange={setLivedrawMacau} />
          <UrlField label='URL Live Draw Singapore Toto' value={livedrawSingaporeToto} onChange={setLivedrawSingaporeToto} />
        </div>
      </div>

      {/* Statistics */}
      <div className='bg-[#1c243b] rounded-lg shadow-lg p-6'>
        <h2 className='text-xl font-semibold mb-4 text-white'>Statistik 7 Hari</h2>
        <div className='h-64 mb-4'>
          <ResponsiveContainer width='100%' height='100%'>
            <AreaChart data={formattedChartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id='colorDeposit' x1='0' y1='0' x2='0' y2='1'>
                  <stop offset='5%' stopColor='#8884d8' stopOpacity={0.8} />
                  <stop offset='95%' stopColor='#8884d8' stopOpacity={0} />
                </linearGradient>
                <linearGradient id='colorWithdraw' x1='0' y1='0' x2='0' y2='1'>
                  <stop offset='5%' stopColor='#82ca9d' stopOpacity={0.8} />
                  <stop offset='95%' stopColor='#82ca9d' stopOpacity={0} />
                </linearGradient>
                <linearGradient id='colorLogin' x1='0' y1='0' x2='0' y2='1'>
                  <stop offset='5%' stopColor='#ffc658' stopOpacity={0.8} />
                  <stop offset='95%' stopColor='#ffc658' stopOpacity={0} />
                </linearGradient>
                <linearGradient id='colorRegister' x1='0' y1='0' x2='0' y2='1'>
                  <stop offset='5%' stopColor='#ff8042' stopOpacity={0.8} />
                  <stop offset='95%' stopColor='#ff8042' stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey='name' stroke='#8a98b3' />
              <YAxis stroke='#8a98b3' />
              <Tooltip contentStyle={{ backgroundColor: '#1c243b', borderColor: '#4a5568', color: 'white' }} />
              <Area type='monotone' dataKey='deposit' stroke='#8884d8' fillOpacity={1} fill='url(#colorDeposit)' />
              <Area type='monotone' dataKey='withdraw' stroke='#82ca9d' fillOpacity={1} fill='url(#colorWithdraw)' />
              <Area type='monotone' dataKey='login' stroke='#ffc658' fillOpacity={1} fill='url(#colorLogin)' />
              <Area type='monotone' dataKey='register' stroke='#ff8042' fillOpacity={1} fill='url(#colorRegister)' />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className='bg-[#212a40] rounded-lg overflow-hidden'>
          <table className='min-w-full'>
            <thead>
              <tr className='bg-[#2a334a]'>
                <th className='py-2 px-4 text-left text-sm font-medium text-gray-300'>Date</th>
                <th className='py-2 px-4 text-left text-sm font-medium text-gray-300'>Deposit</th>
                <th className='py-2 px-4 text-left text-sm font-medium text-gray-300'>Login</th>
                <th className='py-2 px-4 text-left text-sm font-medium text-gray-300'>Withdraw</th>
                <th className='py-2 px-4 text-left text-sm font-medium text-gray-300'>Register</th>
              </tr>
            </thead>
            <tbody className='divide-y divide-[#2d3649]'>
              {data7hari.map((item, index) => (
                <tr key={index} className='bg-[#212a40]'>
                  <td className='py-2 px-4 text-sm text-gray-300'>
                    {new Date(new Date(item.date).getTime() - 7 * 60 * 60 * 1000).toLocaleDateString('id-ID')}
                  </td>
                  <td className='py-2 px-4 text-sm text-gray-300'>{item.deposit}</td>
                  <td className='py-2 px-4 text-sm text-gray-300'>{item.login}</td>
                  <td className='py-2 px-4 text-sm text-gray-300'>{item.withdraw}</td>
                  <td className='py-2 px-4 text-sm text-gray-300'>{item.register}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {HoverPreviewComponent}

      <FloatingSaveButton onClick={save} isLoading={isLoading} />
    </div>
  );
};

export default Home;
